import React, { useMemo } from 'react'
import classnames from 'classnames'

import Button from '@react/react-spectrum/Button'
import Dialog from '@react/react-spectrum/Dialog'
import ModalTrigger from '@react/react-spectrum/ModalTrigger'
import Tooltip from '@react/react-spectrum/Tooltip'
import OverlayTrigger from '@react/react-spectrum/OverlayTrigger'

import Compass from '@react/react-spectrum/Icon/Compass'
import Copy from '@react/react-spectrum/Icon/Copy'
import Delete from '@react/react-spectrum/Icon/Delete'
import Edit from '@react/react-spectrum/Icon/Edit'
import Home from '@react/react-spectrum/Icon/Home'

import RenameScreenDialog from 'components/RenameScreen'

import { useProject, useScreens, useDialog } from 'hooks'
import { FILE_STORAGE } from 'config/constants'

import '../styles/Screen.scss'

export const Screen = ({ screen, isSelected, onSelect }) => {
  const { project, config, canEdit } = useProject()
  const { screens, addScreen, deleteScreen, renameScreen: _renameScreen } = useScreens()
  const { openDialog, closeDialog } = useDialog()

  const screenTemplate = useMemo(
    () =>
      []
        .concat(config.screenTemplates, config.freeformTemplates)
        .filter(Boolean)
        .find(
          (t) =>
            t.template === screen.meta.template && (screen.meta.preset != null ? screen.meta.preset === t.preset : true)
        ) || {},
    // eslint-disable-next-line
    [screen.meta.template, config.screenTemplates]
  )

  const templateLabel = screenTemplate.preset === 'default' ? '' : screenTemplate.label

  const thumbnail = screenTemplate.thumbnail ? (
    <img
      src={`${FILE_STORAGE.BASE_URL}/${FILE_STORAGE.SCREENS_FOLDER}/${screenTemplate.thumbnail}`}
      alt={screen.meta.template}
    />
  ) : (
    <img
      src={`${FILE_STORAGE.BASE_URL}/${FILE_STORAGE.SCREENS_FOLDER}/${config.unknownTemplate}`}
      alt={screen.meta.template}
    />
  )

  const screenStyles = useMemo(() => (screen.editor ? screen.editor.position : {}), [screen.editor])

  const renameScreen = ({ newTitle, newName }) => {
    _renameScreen(screen.meta.name, newName, newTitle)
    onSelect({ ...screen, meta: { ...screen.meta, title: newTitle, name: newName } })()
  }

  const duplicateScreen = () => {
    let template = {
      ...screen,
      title: screen.meta.title + ' Copy',
      name: null,
    }

    addScreen(template)
  }

  return (
    <div className={classnames('Screen', project.meta.type)} onClick={onSelect(screen)} style={screenStyles}>
      <div className={classnames('Screen__actions', { open: isSelected && canEdit })}>
        <OverlayTrigger placement='bottom'>
          <Button
            variant='tool'
            icon={<Edit />}
            onClick={() =>
              openDialog(
                <RenameScreenDialog screen={screen} screens={screens} onRename={renameScreen} onClose={closeDialog} />
              )
            }
          />

          <Tooltip>Rename</Tooltip>
        </OverlayTrigger>
        <OverlayTrigger placement='bottom'>
          <Button variant='tool' icon={<Copy />} onClick={duplicateScreen} />
          <Tooltip>Duplicate</Tooltip>
        </OverlayTrigger>
        <OverlayTrigger placement='bottom'>
          <ModalTrigger>
            <Button variant='tool' quiet icon={<Delete />} />
            <Dialog
              variant='destructive'
              title='Warning'
              confirmLabel='OK'
              cancelLabel='Cancel'
              onConfirm={() => {
                deleteScreen(screen.meta.name)
              }}
            >
              Do you want to delete this screen?
            </Dialog>
          </ModalTrigger>
          <Tooltip>Delete</Tooltip>
        </OverlayTrigger>
      </div>
      <div className={classnames('screen', { active: isSelected })}>{thumbnail}</div>
      <div className='Screen__label'>
        <div className={'Screen__title'}>
          {!!screen.meta.isRootScreen && <Home className={'is-root'} size={'XS'} />}
          {!!screen.meta.includeInNavigation && <Compass size={'XS'} />}
          <span>{screen.meta.title || screen.meta.name}</span>
        </div>
        <div className={'Screen__template'}>{templateLabel}</div>
      </div>
    </div>
  )
}
